/**
 * shieldGate.ts
 * Angular hit-gate for the VoidMothership rotating shield.
 *
 * Pure module — no entity imports, no state mutation. Given the boss's
 * current position, rotation angle, and a projectile-impact point,
 * decides whether the shield's rotating node ring should *block* the
 * hit (player must wait for a gap) or *let it through* (player threaded
 * the gap between two nodes and lands damage).
 *
 * Used by `VoidMothership.takeDamage(amount, hitX?, hitY?)` only while
 * `isShielded` is true and hit coordinates are supplied. When coords
 * are omitted the gate defaults to "blocked" (matches the pre-feature
 * behaviour where every shielded hit was a no-op).
 *
 * The renderer in `spaceDrawBuilders.ts:buildBossShieldRects` paints
 * indicative node squares at fixed pixel size; this module's angular
 * gate is independent of that visual size and is configured via
 * `VOID_MOTHERSHIP_SHIELD_NODE_HALF_ANGULAR_WIDTH_RAD` in
 * `bossConstants.ts`.
 *
 * @module shieldGate
 */

import {
  VOID_MOTHERSHIP_SHIELD_NODE_COUNT,
  VOID_MOTHERSHIP_SHIELD_NODE_HALF_ANGULAR_WIDTH_RAD,
  VOID_MOTHERSHIP_SHIELD_RADIUS_X,
  VOID_MOTHERSHIP_SHIELD_RADIUS_Y,
} from '../../constants/bossConstants';

/**
 * World-space polar angle (radians, normalised to `[0, 2π)`) of node
 * `i` for the current `shieldAngle`. The renderer
 * (`spaceDrawBuilders.buildBossShieldRects`) places each node at
 * `(cos(param) · RADIUS_X, sin(param) · RADIUS_Y)` where
 * `param = shieldAngle + i · 2π/N`. Because the shield ring is an
 * **ellipse** (`RADIUS_X = 72`, `RADIUS_Y = 44`), the node's visual
 * direction from the boss centre is *not* equal to its parameter
 * angle for non-cardinal rotations — they diverge by up to ~13.5°.
 * The gate must compare the projectile's polar angle against the
 * node's *visual* angle, not its parameter angle, otherwise gameplay
 * desynchronises from the rendered ring.
 *
 * DECISION: Centralised here so the formula lives next to the gate
 * logic that consumes it (and the JSDoc that explains it). Exported
 * for test reuse so tests stay locked to the same projection.
 *
 * @param i - Node index in `[0, N)`.
 * @param shieldAngle - Current ring rotation (rad).
 * @returns Node visual angle in `[0, 2π)`.
 */
export function shieldNodeVisualAngle(i: number, shieldAngle: number): number {
  const param =
    shieldAngle + (i * Math.PI * 2) / VOID_MOTHERSHIP_SHIELD_NODE_COUNT;
  return normaliseAngle(
    Math.atan2(
      Math.sin(param) * VOID_MOTHERSHIP_SHIELD_RADIUS_Y,
      Math.cos(param) * VOID_MOTHERSHIP_SHIELD_RADIUS_X,
    ),
  );
}

/**
 * Inputs for the angular shield-gate check. Only the fields the gate
 * actually reads — boss centre + current ring rotation — so call sites
 * don't have to pass a full entity reference.
 */
export interface ShieldGateInput {
  /** Boss centre X in world space. */
  readonly bossCenterX: number;
  /** Boss centre Y in world space. */
  readonly bossCenterY: number;
  /** Current shield-ring rotation (rad), already wrapped to `[0, 2π)`. */
  readonly shieldAngle: number;
  /** Impact X in world space (projectile centre). */
  readonly hitX: number;
  /** Impact Y in world space (projectile centre). */
  readonly hitY: number;
}

/**
 * Returns `true` when the impact at `(hitX, hitY)` falls within the
 * angular coverage of any shield node, i.e. the shield blocks the hit
 * and `takeDamage()` should no-op. Returns `false` when the impact
 * angle threads a gap between two nodes — in that case the caller
 * should apply damage as if EXPOSED.
 *
 * The boss centre is the origin; the impact angle is
 * `atan2(hitY - bossCenterY, hitX - bossCenterX)` normalised into
 * `[0, 2π)`. Each node's centre angle is derived via
 * {@link shieldNodeVisualAngle} (the ellipse-projected on-screen
 * direction, **not** the parameter angle) so the gate stays locked to
 * what the renderer actually paints. The node covers
 * `[centerAngle ± halfWidth]`; the check uses the wrap-safe shortest
 * angular distance so a node straddling `2π → 0` still blocks correctly.
 *
 * DECISION: Returning a boolean instead of consuming `amount` / mutating
 * HP keeps this module pure and unit-testable without entity stubs. The
 * caller (`VoidMothership.takeDamage`) owns the decrement.
 *
 * @param input - Boss + impact geometry. See {@link ShieldGateInput}.
 * @returns `true` if blocked by a node, `false` if the hit lands in a gap.
 */
export function isShieldedHitBlocked(input: ShieldGateInput): boolean {
  const dx = input.hitX - input.bossCenterX;
  const dy = input.hitY - input.bossCenterY;
  // DECISION: Degenerate hit dead-centre on the boss (dx == dy == 0)
  // is treated as blocked — atan2(0, 0) is 0 in JS but the hit has no
  // meaningful direction, so falling back to "shield wins" is the
  // conservative choice that matches the pre-feature behaviour.
  if (dx === 0 && dy === 0) return true;
  const hitAngle = normaliseAngle(Math.atan2(dy, dx));
  for (let i = 0; i < VOID_MOTHERSHIP_SHIELD_NODE_COUNT; i++) {
    const nodeAngle = shieldNodeVisualAngle(i, input.shieldAngle);
    if (
      shortestAngularDistance(hitAngle, nodeAngle) <=
      VOID_MOTHERSHIP_SHIELD_NODE_HALF_ANGULAR_WIDTH_RAD
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Wrap an angle into `[0, 2π)`. JS's `atan2` returns `(-π, π]`; the
 * gate logic compares against node centres which are produced by
 * accumulating positive rotations from 0, so a single normalisation
 * makes the comparison wrap-safe.
 */
function normaliseAngle(rad: number): number {
  const TAU = Math.PI * 2;
  const wrapped = rad % TAU;
  return wrapped < 0 ? wrapped + TAU : wrapped;
}

/**
 * Shortest angular distance between two angles in `[0, 2π)`. The naive
 * `|a − b|` answer breaks across the 2π→0 seam (e.g. distance between
 * 0.1 and 6.18 should be ~0.2, not ~6.08); this returns the minimum of
 * the forward and backward arcs.
 */
function shortestAngularDistance(a: number, b: number): number {
  const TAU = Math.PI * 2;
  const diff = Math.abs(a - b) % TAU;
  return diff > Math.PI ? TAU - diff : diff;
}
